import React, {createContext, useCallback, useContext, useMemo, useState, useEffect} from "react";
import siteConfig from "../config/site";

const origin = window.location.origin;
const BACKEND_ADDR = siteConfig.apiUrl || origin;

const walletContext = createContext();

export default function WalletProvider({children}) {
  const [authenticated, setAuthenticated] = useState(false);
  const [roles, setRoles] = useState(["None"]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshAuth = useCallback(async () => {
    // Skip auth checks if no real backend is configured (prevents local network access prompts)
    const isLocalBackend = BACKEND_ADDR.includes("localhost") || BACKEND_ADDR.includes("127.0.0.1");
    if (isLocalBackend) {
      setLoading(false);
      return;
    }

    setLoading(true);
    let nextRoles = ["None"];
    let nextAuthenticated = false;

    try {
      const response = await fetch(`${BACKEND_ADDR}/auth/checkauth`, {
        "Content-Type": "application/json",
        credentials: "include",
      });

      if (response.ok) {
        const res = await response.json();
        nextAuthenticated = true;
        if (Array.isArray(res.roles)) {
          nextRoles = res.roles;
        } else if (res.role) {
          nextRoles = [res.role];
        }
      }
    } catch (error) {
      console.error("Failed to check auth status:", error);
    }

    try {
      const response = await fetch(`${BACKEND_ADDR}/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const res = await response.json();
        setUser(res.user ?? null);
        if (typeof res.authenticated === "boolean") {
          nextAuthenticated = res.authenticated;
        }
        if (Array.isArray(res.roles)) {
          nextRoles = res.roles;
        } else if (Array.isArray(res.user?.roles)) {
          nextRoles = res.user.roles;
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Failed to load user profile:", error);
      setUser(null);
    }

    setRoles(nextRoles);
    setAuthenticated(nextAuthenticated);
    setLoading(false);
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const logout = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_ADDR}/auth/logout`, {
        "Content-Type": "application/json",
        credentials: "include",
      });
      if (!response.ok) {
        return false;
      }
    } catch (error) {
      console.error("Failed to log out:", error);
      return false;
    } finally {
      setRoles(["None"]);
      setAuthenticated(false);
      setUser(null);
    }
    return true;
  }, []);

  const values = useMemo(() => {
    return {
      authenticated,
      roles,
      user,
      loading,
      refreshAuth,
      logout,
    };
  }, [authenticated, roles, user, loading, refreshAuth, logout]);

  return <walletContext.Provider value={values}>{children}</walletContext.Provider>;
}

export const useWalletProvider = () => useContext(walletContext);
